import { GameOrchestrator } from '../../application/gameOrchestrator.js';
import { buildOrchestratorTickContext } from './orchestratorTickContext.js';
import { startMatchAnimateLoop } from './matchAnimateLoop.js';
import { bootPerfHarness } from '../bootPerfHarness.js';
import { updateEntityProjections } from '../scene/entitySceneManager.js';
import { syncAramFowOverlay } from './syncAramFowOverlay.js';

/**
 * bootMatchLoop — Orchestrator + perf harness + animate loop (keeps app.js slim).
 */
export function bootMatchLoop(deps) {
  const {
    THREE, camera, renderer, scene, inputController,
    matchRuntime, matchOutcomeWire, mapObjectives, entityInspect, minimap,
    activeMinions, activeProjectiles, activeJungleCamps,
    getBlueSpawner, getRedSpawner,
    mapBounds, wallBoxes, bushZones, impassableZones, recentDamageEvents,
    addMinionsToScene, MeshFactory,
    championEntity, dummyEntity, garenEntity,
    tickSummonerSpells, abilityCasterVfx, tickMpPresence,
    tickFighterBuff,
    tickPingHud,
    projectionDeps,
    mapSceneState
  } = deps;

  const gameOrchestrator = new GameOrchestrator();
  const perfDebug = bootPerfHarness({
    renderer, scene, activeMinions, activeProjectiles,
    addMinionsToScene, championEntity, dummyEntity, MeshFactory
  });

  startMatchAnimateLoop({
    THREE, camera, renderer, scene, inputController,
    matchRuntime, matchOutcomeWire, mapObjectives, entityInspect,
    gameOrchestrator, minimap,
    buildTickContext: () => buildOrchestratorTickContext({
      matchRuntime, activeMinions,
      blueSpawner: getBlueSpawner(),
      redSpawner: getRedSpawner(),
      activeProjectiles, activeJungleCamps,
      mapBounds, wallBoxes, bushZones, impassableZones, recentDamageEvents, scene,
      mapObjectives, addMinionsToScene, MeshFactory
    }),
    runOrchestratorTick: (dt, ctx) => {
      const collect = typeof perfDebug.recording === 'function' && perfDebug.recording();
      const result = gameOrchestrator.tick(dt, {
        ...ctx,
        collectPhaseTimings: collect
      });
      if (collect && result.phaseTimingsMs) {
        perfDebug.recordPhaseTimings(result.phaseTimingsMs);
      }
      return result;
    },
    onFrameTick: (dt) => {
      perfDebug.sampleFrame(dt);
      tickSummonerSpells(dt, championEntity);
      abilityCasterVfx.tick(dt);
      if (tickMpPresence) tickMpPresence();
      if (typeof tickPingHud === 'function') tickPingHud(dt);
      if (typeof tickFighterBuff === 'function') tickFighterBuff(dt);
      syncAramFowOverlay({
        sceneState: mapSceneState,
        viewer: championEntity,
        activeWards: matchRuntime?.activeWards || [],
        team: championEntity?.team || 'blue',
        gameTime: matchRuntime?.gameTime,
        THREE,
        activeMinions,
        alliedChamps: [garenEntity]
      });
    },
    projectEntities: typeof deps.projectEntities === 'function'
      ? deps.projectEntities
      : () => updateEntityProjections(projectionDeps())
  });

  return { gameOrchestrator, perfDebug };
}
